import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircuitTripEvent } from '../../types';
import { apiService } from '../../services/api';

interface CircuitBreakerAlertProps {
  tripEvent: CircuitTripEvent | null;
  projectId?: string;
  onDismiss?: () => void;
}

/**
 * CircuitBreakerAlert - Alert banner displayed when a circuit breaker trips
 *
 * Shows:
 * - Queue name
 * - Trip reason
 * - Last error message
 * - Timestamp when circuit was opened
 *
 * Actions:
 * - View Error Queue: Navigate to error queue for inspection
 * - Reset Circuit Breaker: Call API to reset the circuit
 * - Dismiss: Hide the alert
 */
const CircuitBreakerAlert: React.FC<CircuitBreakerAlertProps> = ({
  tripEvent,
  projectId,
  onDismiss,
}) => {
  const navigate = useNavigate();
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Don't render if no trip event
  if (!tripEvent) {
    return null;
  }

  const formatTimestamp = (date: string | Date): string => {
    const d = new Date(date);
    return d.toISOString().replace('T', ' ').slice(0, 19);
  };

  const handleViewErrorQueue = () => {
    const basePath = projectId ? `/projects/${projectId}` : '';
    navigate(`${basePath}/queues/${tripEvent.queue_id}/errors`);
  };

  const handleReset = async () => {
    setIsResetting(true);
    setError(null);

    try {
      await apiService.resetCircuitBreaker(tripEvent.queue_id);
      // Success - dismiss the alert
      onDismiss?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset circuit breaker';
      setError(errorMessage);
    } finally {
      setIsResetting(false);
    }
  };

  const handleDismiss = () => {
    onDismiss?.();
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-800 p-4 mb-4 rounded-r"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-yellow-400"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
            Circuit Breaker Tripped: {tripEvent.queue_name}
          </h3>
          <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
            <p className="mb-1">
              <span className="font-medium">Reason: </span>
              {tripEvent.reason}
            </p>
            {tripEvent.last_error_message && (
              <p className="mb-1">
                <span className="font-medium">Last Error: </span>
                {tripEvent.last_error_message}
              </p>
            )}
            <p className="mb-1">
              <span className="font-medium">Tripped at: </span>
              {formatTimestamp(tripEvent.opened_at)}
            </p>
          </div>
          {error && (
            <div className="mt-2 text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}
          <div className="mt-4 flex space-x-3">
            <button
              type="button"
              onClick={handleViewErrorQueue}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-yellow-800 dark:text-yellow-300 bg-yellow-200 dark:bg-yellow-900/30 hover:bg-yellow-300 dark:hover:bg-yellow-900/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              View Error Queue
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={isResetting}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResetting ? 'Resetting...' : 'Reset Circuit Breaker'}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CircuitBreakerAlert;
